import type { FC } from "react";

/// <reference types="@types/google.maps" />
import "@googlemaps/js-api-loader";
import { camelToStartCase, logError } from "@repo/helpers";
import { Button, ButtonTypes } from "@repo/ui";
import { Color, getFilterValuesForHex } from "@repo/ui/charts/googleMaps/color";
import "@repo/ui/src/charts/googleMaps/index.css";
import { dedupeByCoordinates } from "@repo/ui/charts/googleMaps/utils";
import GoogleMapReact from "google-map-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";

import styles from "./style.module.scss";

type Label = Record<string, string>;

interface Link {
	href: string;
	label: string;
	value: string | number;
}

export interface Marker {
	id: number | string;
	group?: string;
	datetime?: Date;
	lat: number;
	lon?: number;
	lng?: number;
	label: Label[];
	image: string;
	tooltip?: string;
	links?: Link[];
	color?: string;
	scale?: number;
	ordinal: number;
}

interface MapChartProps {
	id: string;
	data: Marker[];
	type: "map" | "heatmap";
	height?: number | string;
	onExportReady?: () => void;
	onExportError?: () => void;
}

interface CustomMarker extends google.maps.marker.AdvancedMarkerElement {
	key: string;
	data: Marker;
}

// Keep MyView map behavior local so changes cannot affect existing shared map consumers.
export const WidgetMapChart: FC<MapChartProps> = ({
	id,
	data = [],
	type,
	height = 372,
	onExportReady,
	onExportError,
}) => {
	const [mapRef, setMapRef] = useState<google.maps.Map | null>(null);
	const [displayMarkers, setDisplayMarkers] = useState<boolean>(false);
	const [markers, setMarkers] = useState<CustomMarker[]>([]);
	const [initialized, setInitialized] = useState<boolean>(false);

	const filteredData = useMemo(() => {
		return dedupeByCoordinates(data);
	}, [data]);

	useEffect(() => {
		if (initialized) return;
		if (markers?.length) setInitialized(true);
	}, [initialized, markers]);

	const center = USA_CENTER;

	const computedBounds = useMemo(() => {
		if (!window.google?.maps) return undefined;
		//If some data is loading, but we've already initialized, don't reset bounds
		if (!markers?.length) {
			return initialized ? undefined : USA_CENTER_BOUNDS();
		}
		const b = new window.google.maps.LatLngBounds();
		markers.forEach((item) => {
			b.extend(item.position);
		});

		return b;
	}, [markers, initialized]);

	const onGoogleApiLoaded = useCallback(({ map }: { map: google.maps.Map }) => {
		setMapRef(map);
		setDisplayMarkers(true);
	}, []);

	const heatmapData = useMemo(() => {
		return {
			positions: data.map((item) => ({
				lat: item.lat,
				lng: item.lon ?? item.lng ?? 0,
				weight: 100,
			})),
			options: {},
		};
	}, [data]);

	useEffect(() => {
		if (!mapRef) return;
		if (!computedBounds) return;

		mapRef.fitBounds(computedBounds);
	}, [computedBounds, mapRef]);

	useEffect(() => {
		if (!displayMarkers) return;
		if (!mapRef) return;

		if (!filteredData?.length) {
			markers.forEach((marker) => (marker.map = null));
			setMarkers([]);
			return;
		}
		fullUpdateMarker().catch((error) => {
			logError(error);
			onExportError?.();
		});
	}, [filteredData, type, displayMarkers, mapRef]);

	async function fullUpdateMarker() {
		const { InfoWindow } = (await google.maps.importLibrary("maps")) as google.maps.MapsLibrary;
		const { AdvancedMarkerElement } = (await google.maps.importLibrary("marker")) as google.maps.MarkerLibrary;

		const newInfoWindow = new InfoWindow();

		const currentMarkerMap = new Map<string | number, CustomMarker>(markers.map((marker) => [marker.key, marker]));

		const filteredDataMap = new Map(filteredData.map((item) => [item.id, item]));

		// Remove markers that aren't in filtered data
		for (const marker of currentMarkerMap.values()) {
			if (!filteredDataMap.has(marker.key)) {
				marker.map = null;
				currentMarkerMap.delete(marker.key);
			}
		}

		// Update existing markers and add new ones
		for (const item of filteredDataMap.values()) {
			if (!currentMarkerMap.has(item.id)) {
				const newMarker: CustomMarker = createMarker(item, item.id, newInfoWindow, AdvancedMarkerElement);
				currentMarkerMap.set(item.id, newMarker);
			} else {
				const marker = currentMarkerMap.get(item.id);
				if (marker) {
					marker.data = item;
					marker.map = type === "heatmap" ? null : mapRef;
					marker.position = { lat: item.lat, lng: item.lon ?? item.lng! };
					const img = marker.content as HTMLImageElement;
					if (img) applyMarkerAppearance(img, item);
				}
			}
		}

		// Update markers state with current set
		setMarkers(Array.from(currentMarkerMap.values()));
		if (onExportReady) {
			const bounds = new google.maps.LatLngBounds();
			filteredData.forEach((item) => bounds.extend({ lat: item.lat, lng: item.lon ?? item.lng! }));
			google.maps.event.addListenerOnce(mapRef!, "idle", () => {
				const images = Array.from(document.querySelectorAll<HTMLImageElement>(`#${id} img`));
				Promise.all(images.map((img) => img.decode()))
					.then(onExportReady)
					.catch(() => onExportError?.());
			});
			mapRef!.fitBounds(bounds);
		}
	}

	function applyMarkerAppearance(flagImage: HTMLImageElement, item: Marker) {
		if (flagImage.src !== item.image) flagImage.src = item.image;

		const base = 32;
		const scale = item.scale ?? 1;
		const size = Math.round(base * scale);
		if (flagImage.width !== size || flagImage.height !== size) {
			flagImage.width = size;
			flagImage.height = size;
		}
		flagImage.title = item.tooltip;

		if (item.group || item.color) {
			flagImage.classList.add("marker-image");
			const color = Color.fromString(item.group ?? "");
			const filter = getFilterValuesForHex(item.color ?? color.toHex());

			flagImage.style.setProperty("--invert", `${filter.values.invert.toFixed(2)}%`);
			flagImage.style.setProperty("--sepia", `${filter.values.sepia.toFixed(2)}%`);
			flagImage.style.setProperty("--saturate", `${filter.values.saturate.toFixed(2)}%`);
			flagImage.style.setProperty("--hue-rotate", `${(filter.values.hueRotate * 3.6).toFixed(2)}deg`);
			flagImage.style.setProperty("--brightness", `${filter.values.brightness.toFixed(2)}%`);
			flagImage.style.setProperty("--contrast", `${filter.values.contrast.toFixed(2)}%`);
		} else {
			flagImage.classList.remove("marker-image");
		}
	}

	function createMarker(
		item: Marker,
		key: string | number,
		newInfoWindow: google.maps.InfoWindow,
		AdvancedMarkerElement: any,
	) {
		// Create new marker
		const flagImage = document.createElement("img");
		applyMarkerAppearance(flagImage, item);

		const marker = new AdvancedMarkerElement({
			id: key,
			position: { lat: item.lat, lng: item.lon ?? item.lng! },
			map: type === "heatmap" ? null : mapRef,
			content: flagImage,
			gmpClickable: true,
		});

		// Store key for future reference
		marker.key = key;
		marker.data = item;

		// Add animation for new markers
		const content = marker.content as HTMLElement;
		content.style.opacity = "0";
		content.classList.add("drop");
		content.addEventListener("animationend", () => {
			content.classList.remove("drop");
			content.style.opacity = "1";
		});

		// Add marker click handler (rest of the click handler code remains the same)
		marker.addListener("click", () => {
			const item: Marker = marker.data;
			const currentBounds = mapRef?.getBounds();
			const currentZoom = mapRef?.getZoom();

			mapRef?.setMapTypeId("satellite");
			mapRef?.setCenter({ lat: item.lat, lng: item.lon ?? item.lng! });
			mapRef?.setZoom(19);

			newInfoWindow.close();
			newInfoWindow.setContent(
				`<div style="color: black">
					<dl style="display: grid; grid-template-columns: 1fr 2fr; gap: 4px; padding-bottom: 4px">
						${
							item.links
								?.map((l) => {
									return `<dt style="font-weight: bold">${l.label}:</dt><dd><a href="${l.href}" target="_blank" style="color: blue; text-decoration: underline">${l.value}</a></dd>`;
								})
								.join("") ?? ""
						}
						${item.label
							.map((l) => {
								return Object.keys(l)
									.map((k) => {
										return `<dt style="font-weight: bold">${camelToStartCase(k)}:</dt><dd>${l[k]}</dd>`;
									})
									.join("");
							})
							.join("")}
					</dl>
					<div style="display: flex; flex-direction: column; align-items: start; gap: 4px">
						${renderToStaticMarkup(
							<Button id="openStreetView" types={[ButtonTypes.Text]}>
								Open Street View
							</Button>,
						)}
					</div>
				</div>`,
			);

			newInfoWindow.open(mapRef, marker);

			newInfoWindow.addListener("closeclick", () => {
				mapRef?.setMapTypeId("roadmap");

				if (currentBounds) {
					mapRef?.fitBounds(currentBounds);
					mapRef?.setZoom(currentZoom);
				} else {
					mapRef?.setCenter({ lat: item.lat, lng: item.lon ?? item.lng! });
					mapRef?.setZoom(11);
				}
			});

			newInfoWindow.addListener("domready", () => {
				const openStreetViewButton = document.getElementById("openStreetView");
				if (!openStreetViewButton) return;
				openStreetViewButton.addEventListener("click", () => openStreetView(item.lat, item.lon ?? item.lng!));
			});

			function openStreetView(lat: number, lng: number) {
				if (!mapRef) return;

				const panorama: google.maps.StreetViewPanorama = mapRef?.getStreetView();

				panorama.setPosition({ lat, lng });
				panorama.setVisible(true);

				const size = new google.maps.Size(96, 96);
				const image = {
					url: flagImage.src,
					size,
					scaledSize: size,
				} as google.maps.Icon;

				const legacyMarker = new google.maps.Marker({
					position: { lat, lng },
					title: "",
					icon: image,
				});

				legacyMarker.setMap(panorama);

				mapRef.setStreetView(panorama);

				panorama.addListener("closeclick", () => {
					legacyMarker.setMap(null);
				});
			}
		});
		return marker;
	}

	return (
		<div id={id} className={styles.mapChart} style={{ height }}>
			<GoogleMapReact
				bootstrapURLKeys={{
					// TODO: don't hard code this
					key: "AIzaSyDcKVNzjWpSbwSvi8lig6-b5m9iXn7I1zI",
					libraries: ["visualization", "marker"],
				}}
				options={{
					minZoom: 3,
					mapTypeId: "roadmap",
					mapTypeControl: true,
					scaleControl: false,
					streetViewControl: false,
					rotateControl: true,
					fullscreenControl: true,
					zoomControl: true,
					clickableIcons: false,
					mapId: "4504f8b37365c3d0",
				}}
				defaultCenter={center}
				defaultZoom={3}
				heatmapLibrary={type === "heatmap"}
				heatmap={type === "heatmap" ? heatmapData : undefined}
				onGoogleApiLoaded={onGoogleApiLoaded}
				yesIWantToUseGoogleMapApiInternals
				shouldUnregisterMapOnUnmount
			/>
		</div>
	);
};

/** Geographic center of the USA */
const USA_CENTER = { lat: 39.8282, lng: -98.5795 };
const USA_CENTER_BOUNDS = () =>
	google.maps.LatLngBounds &&
	new google.maps.LatLngBounds().extend({ lat: 24.5205, lng: -125.0 }).extend({ lat: 49.3844, lng: -66.9346 });
